import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { restoreKnownGoodBundle } from "../offline/database.js";
import { useI18n } from "../i18n/I18nProvider.js";
import type { TranslationKey } from "../i18n/translations.js";
import { resolveDisasterContent } from "./contentResolver.js";
import type { DisasterContentResolution } from "./contentResolver.js";
import {
  initialDisasterSession,
  inspectDisasterSession,
  transitionDisasterSession,
} from "./machine.js";
import type { DisasterAnswer, DisasterEvent, DisasterSession, DisasterStep } from "./machine.js";
import { loadDisasterSession, saveDisasterSession } from "./sessionRepository.js";

interface Choice {
  readonly answer: DisasterAnswer;
  readonly key: TranslationKey;
}

const choices: Readonly<Partial<Record<DisasterStep, readonly Choice[]>>> = {
  EVENT: [
    { answer: "FICTIONAL_EVENT_A", key: "disaster.choice.event_a" },
    { answer: "FICTIONAL_EVENT_B", key: "disaster.choice.event_b" },
  ],
  CONTEXT: [
    { answer: "CONTEXT_INDOOR", key: "disaster.choice.indoor" },
    { answer: "CONTEXT_OUTDOOR", key: "disaster.choice.outdoor" },
  ],
  IMMEDIATE_DANGER: [
    { answer: "DANGER_YES", key: "disaster.choice.danger_yes" },
    { answer: "DANGER_NO", key: "disaster.choice.danger_no" },
  ],
  DECISION: [
    { answer: "BRANCH_ALPHA", key: "disaster.choice.branch_a" },
    { answer: "BRANCH_BETA", key: "disaster.choice.branch_b" },
  ],
  NEXT_ACTION: [{ answer: "ACKNOWLEDGE", key: "disaster.choice.acknowledge" }],
  CONTINUATION: [
    { answer: "CONTINUE", key: "disaster.choice.continue" },
    { answer: "FINISH", key: "disaster.choice.finish" },
  ],
};

const titles: Record<DisasterStep, TranslationKey> = {
  ENTRY: "disaster.entry.title",
  EVENT: "disaster.step.event",
  CONTEXT: "disaster.step.context",
  IMMEDIATE_DANGER: "disaster.step.danger",
  DECISION: "disaster.step.decision",
  NEXT_ACTION: "disaster.step.action",
  CONTINUATION: "disaster.step.continuation",
  COMPLETE: "disaster.step.complete",
};

const bodies: Record<DisasterStep, TranslationKey> = {
  ENTRY: "disaster.entry.body",
  EVENT: "disaster.body.event",
  CONTEXT: "disaster.body.context",
  IMMEDIATE_DANGER: "disaster.body.danger",
  DECISION: "disaster.body.decision",
  NEXT_ACTION: "disaster.body.action",
  CONTINUATION: "disaster.body.continuation",
  COMPLETE: "disaster.body.complete",
};

export function DisasterMode() {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const heading = useRef<HTMLHeadingElement>(null);
  const [session, setSession] = useState<DisasterSession>();
  const [resolution, setResolution] = useState<DisasterContentResolution>();
  const [recoveryAttempt, setRecoveryAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    void loadDisasterSession().then((saved) => {
      if (active) setSession(saved);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (session !== undefined) heading.current?.focus();
  }, [session?.current]);

  useEffect(() => {
    if (session === undefined || session.current === "ENTRY") {
      setResolution(undefined);
      return;
    }
    let active = true;
    setResolution(undefined);
    void resolveDisasterContent(locale, session).then((result) => {
      if (active) setResolution(result);
    });
    return () => {
      active = false;
    };
  }, [locale, recoveryAttempt, session]);

  if (session === undefined) {
    return <p role="status">{t("disaster.loading")}</p>;
  }

  const diagnostics = inspectDisasterSession(session);
  const apply = (event: DisasterEvent) => {
    const next = transitionDisasterSession(session, event);
    setSession(next);
    void saveDisasterSession(next);
  };
  const restart = () => {
    setSession(initialDisasterSession);
    void saveDisasterSession(initialDisasterSession);
  };
  const exit = () => navigate("/");
  const useKnownGood = async () => {
    await restoreKnownGoodBundle(locale);
    setRecoveryAttempt((attempt) => attempt + 1);
  };
  const staticChoices = choices[session.current] ?? [];
  const resolvedChoices = resolution?.ok
    ? resolution.value.binding.screen.options.map((option) => ({
        answer: option.answerId as DisasterAnswer,
        label: option.label,
      }))
    : [];
  const isEntry = session.current === "ENTRY";
  const isResolving = !isEntry && resolution === undefined;
  const unavailable = resolution !== undefined && !resolution.ok;
  const resolved = resolution?.ok ? resolution.value : undefined;

  return (
    <article className={`disaster-mode disaster-mode--${session.current.toLowerCase()}`}>
      <header className="disaster-mode__header">
        <span className="disaster-mode__badge">
          {resolved?.status === "STALE_VERIFIED"
            ? t("binding.status.stale")
            : resolved === undefined
              ? t("disaster.fixture_badge")
              : t("binding.status.ready")}
        </span>
        {session.current !== "ENTRY" && (
          <p aria-live="polite" className="disaster-mode__progress">
            {t("disaster.progress")} {Math.min(diagnostics.progressIndex, 6)} / 6
          </p>
        )}
      </header>
      <div className="disaster-mode__content">
        <h1 ref={heading} tabIndex={-1}>
          {unavailable
            ? t("binding.unavailable.title")
            : (resolved?.binding.screen.title ?? t(titles[session.current]))}
        </h1>
        <p>
          {unavailable
            ? t(`binding.reason.${resolution.reason}` as TranslationKey)
            : (resolved?.binding.screen.context ?? t(bodies[session.current]))}
        </p>

        {isEntry ? (
          <button
            className="disaster-choice disaster-choice--primary"
            onClick={() => apply({ type: "START" })}
          >
            {t("disaster.start")}
          </button>
        ) : isResolving ? (
          <p aria-live="polite" className="binding-loading" role="status">
            {t("binding.loading")}
          </p>
        ) : unavailable ? (
          <div className="binding-recovery-actions">
            {diagnostics.canGoBack && (
              <button className="disaster-choice" onClick={() => apply({ type: "BACK" })}>
                {t("binding.return")}
              </button>
            )}
            {resolution.knownGoodAvailable && (
              <button className="disaster-choice" onClick={() => void useKnownGood()}>
                {t("binding.use_known_good")}
              </button>
            )}
            <button className="disaster-choice" onClick={restart}>
              {t("disaster.restart")}
            </button>
          </div>
        ) : session.current === "COMPLETE" ? (
          <button className="disaster-choice disaster-choice--primary" onClick={exit}>
            {t("disaster.exit")}
          </button>
        ) : (
          <div aria-label={t("disaster.options")} className="disaster-mode__choices" role="group">
            {(resolved === undefined ? staticChoices : resolvedChoices).map((choice) => (
              <button
                className="disaster-choice"
                key={choice.answer}
                onClick={() => apply({ type: "ANSWER", answer: choice.answer })}
              >
                {"key" in choice ? t(choice.key) : choice.label}
                <span aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        )}
        {resolved?.binding.screen.actionPanel !== undefined && (
          <aside className="binding-action-panel">{resolved.binding.screen.actionPanel}</aside>
        )}
        {resolved?.binding.screen.continuationPrompt !== undefined && (
          <p className="binding-continuation">{resolved.binding.screen.continuationPrompt}</p>
        )}
        {resolved !== undefined && (
          <footer className="binding-provenance">
            <strong>{t("binding.provenance")}</strong> {resolved.binding.screen.provenanceLabel}
            <span>
              {t("binding.reviewed")} {resolved.binding.screen.reviewedAt.slice(0, 10)}
            </span>
          </footer>
        )}
      </div>
      <nav aria-label={t("disaster.controls")} className="disaster-mode__controls">
        <button disabled={!diagnostics.canGoBack} onClick={() => apply({ type: "BACK" })}>
          {t("disaster.back")}
        </button>
        <button onClick={restart}>{t("disaster.restart")}</button>
        <button onClick={exit}>{t("disaster.exit")}</button>
      </nav>
      <details className="disaster-diagnostics">
        <summary>{t("disaster.diagnostics")}</summary>
        <dl>
          <div>
            <dt>{t("disaster.diagnostics_state")}</dt>
            <dd>{diagnostics.state}</dd>
          </div>
          <div>
            <dt>{t("disaster.diagnostics_events")}</dt>
            <dd>{diagnostics.permittedEvents.join(", ")}</dd>
          </div>
          <div>
            <dt>{t("disaster.diagnostics_history")}</dt>
            <dd>{session.history.join(" → ") || "—"}</dd>
          </div>
          <div>
            <dt>{t("binding.diagnostics_bundle")}</dt>
            <dd>{resolved?.bundleId ?? (unavailable ? resolution.bundleId : undefined) ?? "—"}</dd>
          </div>
          <div>
            <dt>{t("binding.diagnostics_locale")}</dt>
            <dd>{locale}</dd>
          </div>
          <div>
            <dt>{t("binding.diagnostics_revision")}</dt>
            <dd>
              {resolved?.revisionId ?? (unavailable ? resolution.revisionId : undefined) ?? "—"}
            </dd>
          </div>
          <div>
            <dt>{t("binding.diagnostics_eligibility")}</dt>
            <dd>
              {resolved?.eligibilityResult ?? (unavailable ? resolution.reason : undefined) ?? "—"}
            </dd>
          </div>
        </dl>
      </details>
    </article>
  );
}
